import axios from 'axios';
import i18next from 'i18next';
import { CalendarEvent } from '@/common/model/events';
import { FollowerCalendar } from '@/common/model/follow';
import ModelService from '@/client/service/models';
import {
  InvalidRemoteCalendarIdentifierError,
  InvalidRepostPolicyError,
  InvalidRepostPolicySettingsError,
  InvalidSharedEventUrlError,
  FollowRelationshipNotFoundError,
  RemoteCalendarNotFoundError,
  RemoteDomainUnreachableError,
  ActivityPubNotSupportedError,
  RemoteProfileFetchError,
  SelfFollowError,
  AlreadyFollowingError,
} from '@/common/exceptions/activitypub';
import { InsufficientCalendarPermissionsError } from '@/common/exceptions/calendar';
import { handleApiError } from '@/client/service/utils';
import { useFeedStore } from '@/client/stores/feedStore';

const errorMap = {
  InvalidRemoteCalendarIdentifierError,
  InvalidRepostPolicyError,
  InvalidRepostPolicySettingsError,
  InvalidSharedEventUrlError,
  FollowRelationshipNotFoundError,
  RemoteCalendarNotFoundError,
  RemoteDomainUnreachableError,
  ActivityPubNotSupportedError,
  RemoteProfileFetchError,
  SelfFollowError,
  AlreadyFollowingError,
  InsufficientCalendarPermissionsError,
};

/**
 * Follow relationship returned from API with new boolean fields
 */
export interface FollowRelationship {
  id: string;
  calendarActorId: string;
  calendarActorUuid: string;
  calendarId: string;
  autoRepostOriginals: boolean;
  autoRepostReposts: boolean;
}

/**
 * Follower relationship returned from API
 */
export interface FollowerRelationship {
  id: string;
  calendarActorId: string;
  calendarId: string;
}

/**
 * Feed event extends CalendarEvent with repost status and source calendar identifier
 */
export type FeedEvent = CalendarEvent & {
  repostStatus: 'none' | 'manual' | 'auto';
  sourceCalendarActorId: string | null;
  categoryIds?: string[];
};

/**
 * Feed response with pagination
 */
export interface FeedResponse {
  events: FeedEvent[];
  hasMore: boolean;
}

/**
 * Remote calendar preview data
 */
export interface RemoteCalendarPreview {
  name: string;
  description?: string;
  domain: string;
  actorUrl: string;
  calendarId?: string;
}

/**
 * A single category entry from the source or local calendar
 */
export interface CategoryEntry {
  id: string;
  name: string;
}

/**
 * A single category mapping entry
 */
export interface CategoryMappingEntry {
  sourceCategoryId: string;
  sourceCategoryName: string;
  localCategoryId: string;
}

/**
 * Client service for feed and social operations
 */
export default class FeedService {
  private _store?: ReturnType<typeof useFeedStore>;

  constructor(store?: ReturnType<typeof useFeedStore>) {
    this._store = store;
  }

  /**
   * Lazily access the feed store. Only initializes when first accessed,
   * allowing HTTP-only methods to work without an active Pinia instance.
   */
  private get store(): ReturnType<typeof useFeedStore> {
    if (!this._store) {
      this._store = useFeedStore();
    }
    return this._store;
  }

  /**
   * Whether a response for `calendarId` may still be written to the store.
   * The store holds one calendar's feed at a time; a response that arrives
   * after the user switched to another calendar would otherwise land on the
   * new calendar's page, so the load* methods drop it.
   */
  private isSelectedCalendar(calendarId: string): boolean {
    return this.store.selectedCalendarId === calendarId;
  }

  /**
   * Load follows from the API and update the store, unless another calendar
   * was selected while the request was in flight.
   * @param calendarId The calendar ID to load follows for
   * @returns Promise<FollowRelationship[]> The loaded follows
   */
  async loadFollows(calendarId: string): Promise<FollowRelationship[]> {
    const follows = await this.getFollows(calendarId);
    if (this.isSelectedCalendar(calendarId)) {
      this.store.setFollows(follows);
    }
    return follows;
  }

  /**
   * Load followers from the API and update the store, unless another
   * calendar was selected while the request was in flight.
   * @param calendarId The calendar ID to load followers for
   * @returns Promise<FollowerRelationship[]> The loaded followers
   */
  async loadFollowers(calendarId: string): Promise<FollowerRelationship[]> {
    const followers = await this.getFollowers(calendarId);
    if (this.isSelectedCalendar(calendarId)) {
      this.store.setFollowers(followers);
    }
    return followers;
  }

  /**
   * Load the initial page of feed events and update the store, unless
   * another calendar was selected while the request was in flight.
   * @param calendarId The calendar ID to load feed for
   * @param pageSize The number of events per page
   * @returns Promise<FeedResponse> The loaded feed response
   */
  async loadFeed(calendarId: string, pageSize: number = 20): Promise<FeedResponse> {
    const response = await this.getFeed(calendarId, 0, pageSize);
    if (this.isSelectedCalendar(calendarId)) {
      this.store.setEvents(response.events);
      this.store.setEventsHasMore(response.hasMore);
    }
    return response;
  }

  /**
   * Load the next page of feed events and append to the store, unless
   * another calendar was selected while the request was in flight.
   * @param calendarId The calendar ID to load feed for
   * @param page The page number to load
   * @param pageSize The number of events per page
   * @returns Promise<FeedResponse> The loaded feed response
   */
  async loadMoreFeed(calendarId: string, page: number, pageSize: number = 20): Promise<FeedResponse> {
    const response = await this.getFeed(calendarId, page, pageSize);
    if (this.isSelectedCalendar(calendarId)) {
      this.store.appendEvents(response.events, page);
      this.store.setEventsHasMore(response.hasMore);
    }
    return response;
  }

  /**
   * Get list of calendars the user follows
   * @param calendarId The calendar ID to get follows for
   * @returns Promise<FollowRelationship[]> Array of follow relationships
   */
  async getFollows(calendarId: string): Promise<FollowRelationship[]> {
    try {
      const data = await ModelService.listModels(`/api/v1/social/follows?calendarId=${calendarId}`);
      return data.items.map(item => ({
        id: item.id,
        calendarActorId: item.calendarActorId,
        calendarActorUuid: item.calendarActorUuid ?? '',
        calendarId: item.calendarId,
        autoRepostOriginals: item.autoRepostOriginals ?? false,
        autoRepostReposts: item.autoRepostReposts ?? false,
      }));
    }
    catch (error) {
      console.error('Error fetching follows:', error);
      throw error;
    }
  }

  /**
   * Get list of calendars following the user
   * @param calendarId The calendar ID to get followers for
   * @returns Promise<FollowerRelationship[]> Array of follower relationships
   */
  async getFollowers(calendarId: string): Promise<FollowerRelationship[]> {
    try {
      const data = await ModelService.listModels(`/api/v1/social/followers?calendarId=${calendarId}`);
      return data.items.map(item => FollowerCalendar.fromObject(item));
    }
    catch (error) {
      console.error('Error fetching followers:', error);
      throw error;
    }
  }

  /**
   * Get feed events from followed calendars
   * @param calendarId The calendar ID to get feed for
   * @param page The page number (0-indexed)
   * @param pageSize The number of events per page
   * @returns Promise<FeedResponse> Feed events with pagination info
   */
  async getFeed(calendarId: string, page: number = 0, pageSize: number = 20): Promise<FeedResponse> {
    try {
      const data = await ModelService.getModel(
        `/api/v1/social/feed?calendarId=${calendarId}&page=${page}&pageSize=${pageSize}`,
      );

      if (!data) {
        return { events: [], hasMore: false };
      }

      // Convert API response to CalendarEvent objects with repostStatus and sourceCalendarActorId
      const events: FeedEvent[] = data.events.map((eventData: any) => {
        const event = CalendarEvent.fromObject(eventData);
        return Object.assign(event, {
          repostStatus: eventData.repostStatus,
          sourceCalendarActorId: eventData.sourceCalendarActorId ?? null,
          categoryIds: Array.isArray(eventData.categoryIds) ? eventData.categoryIds : undefined,
        });
      });

      return {
        events,
        hasMore: data.hasMore,
      };
    }
    catch (error) {
      console.error('Error fetching feed:', error);
      throw error;
    }
  }

  /**
   * Follow a remote calendar
   * @param calendarId The local calendar ID
   * @param remoteIdentifier The remote calendar identifier (e.g., calendar@domain.com)
   * @param autoRepostOriginals Whether to auto-repost original events (default: false)
   * @param autoRepostReposts Whether to auto-repost shared events (default: false)
   * @returns Promise<void>
   */
  async followCalendar(
    calendarId: string,
    remoteIdentifier: string,
    autoRepostOriginals: boolean = false,
    autoRepostReposts: boolean = false,
  ): Promise<void> {
    try {
      await axios.post('/api/v1/social/follows', {
        calendarId,
        remoteCalendar: remoteIdentifier,
        autoRepostOriginals,
        autoRepostReposts,
      });
    }
    catch (error: unknown) {
      console.error('Error following calendar:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Unfollow a remote calendar
   * @param calendarId The local calendar ID
   * @param calendarActorId The calendar actor identifier to unfollow
   * @returns Promise<void>
   */
  async unfollowCalendar(calendarId: string, calendarActorId: string): Promise<void> {
    try {
      await ModelService.delete(`/api/v1/social/follows/${encodeURIComponent(calendarActorId)}?calendarId=${calendarId}`);
    }
    catch (error: unknown) {
      console.error('Error unfollowing calendar:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Manually repost an event to the calendar, optionally tagging it with local category IDs.
   * @param calendarId The calendar ID to repost to
   * @param eventSourceUrl The ActivityPub URL of the event to repost
   * @param categoryIds Optional list of local category IDs to assign when reposting
   * @returns Promise<void>
   */
  async shareEvent(calendarId: string, eventSourceUrl: string, categoryIds: string[] = []): Promise<void> {
    try {
      // Using a simple object since SharedEvent isn't a PrimaryModel on the client
      await ModelService.createModel(
        {
          id: '',
          calendarId,
          eventId: eventSourceUrl,
          toObject: () => ({ calendarId, eventId: eventSourceUrl, categoryIds }),
        } as any,
        '/api/v1/social/shares',
      );
    }
    catch (error: unknown) {
      console.error('Error sharing event:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Remove a reposted event from the calendar
   * @param calendarId The calendar ID to remove repost from
   * @param eventId The event ID to un-repost
   * @returns Promise<void>
   */
  async unshareEvent(calendarId: string, eventId: string): Promise<void> {
    try {
      await ModelService.delete(`/api/v1/social/shares/${encodeURIComponent(eventId)}?calendarId=${calendarId}`);
    }
    catch (error: unknown) {
      console.error('Error unsharing event:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Update the repost policy for a follow relationship
   * @param followId The follow relationship ID
   * @param autoRepostOriginals Whether to auto-repost original events
   * @param autoRepostReposts Whether to auto-repost shared events
   * @param calendarId The calendar ID that owns the follow relationship
   * @returns Promise<FollowRelationship> The updated relationship
   */
  async updateFollowPolicy(
    followId: string,
    autoRepostOriginals: boolean,
    autoRepostReposts: boolean,
    calendarId: string,
  ): Promise<FollowRelationship> {
    try {
      const response = await axios.patch(`/api/v1/social/follows/${encodeURIComponent(followId)}`, {
        calendarId,
        autoRepostOriginals,
        autoRepostReposts,
      });
      return response.data;
    }
    catch (error: unknown) {
      console.error('Error updating follow policy:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Lookup a remote calendar by identifier
   * @param identifier The remote calendar identifier (e.g., calendar@domain.com)
   * @returns Promise<RemoteCalendarPreview> Preview data for the remote calendar
   */
  async lookupRemoteCalendar(identifier: string): Promise<RemoteCalendarPreview> {
    try {
      const data = await ModelService.getModel(`/api/v1/social/lookup?identifier=${encodeURIComponent(identifier)}`);
      if (!data) {
        throw new RemoteCalendarNotFoundError();
      }
      return data as RemoteCalendarPreview;
    }
    catch (error: unknown) {
      console.error('Error looking up remote calendar:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Get source categories for a followed calendar actor.
   * Returns { id, name } entries for each source category.
   * @param calendarId The local calendar ID
   * @param actorId The followed actor identifier
   * @returns Promise<CategoryEntry[]> Array of source categories
   */
  async getSourceCategories(calendarId: string, actorId: string): Promise<CategoryEntry[]> {
    const response = await axios.get(
      `/api/v1/calendars/${encodeURIComponent(calendarId)}/following/${encodeURIComponent(actorId)}/source-categories`,
    );
    return response.data;
  }

  /**
   * Get the local categories for a calendar.
   * Returns { id, name } entries extracted from the translated content.
   * @param calendarId The local calendar ID
   * @returns Promise<CategoryEntry[]> Array of local categories
   */
  async getCalendarCategories(calendarId: string): Promise<CategoryEntry[]> {
    const response = await axios.get(
      `/api/v1/calendars/${encodeURIComponent(calendarId)}/categories`,
    );
    return response.data.map((c: any) => {
      // content is an object keyed by language, e.g. { en: { language: 'en', name: '...' } }
      const contentValues = c.content ? Object.values(c.content) : [];
      const lang = i18next.resolvedLanguage ?? 'en';
      const content = c.content?.[lang] ?? c.content?.['en'] ?? contentValues[0];
      const name = (content as any)?.name ?? '';
      return {
        id: c.id,
        name,
      };
    });
  }

  /**
   * Create a new local category for a calendar.
   * Uses the current UI language for the category name.
   * @param calendarId The local calendar ID
   * @param name The display name for the new category
   * @returns Promise<CategoryEntry> The newly created category with id and name
   */
  async createLocalCategory(calendarId: string, name: string): Promise<CategoryEntry> {
    const lang = i18next.resolvedLanguage ?? 'en';
    const response = await axios.post(
      `/api/v1/calendars/${encodeURIComponent(calendarId)}/categories`,
      { content: { [lang]: { name } } },
    );
    return {
      id: response.data.id,
      name,
    };
  }

  /**
   * Get existing category mappings for a followed calendar actor.
   * @param calendarId The local calendar ID
   * @param actorId The followed actor identifier
   * @returns Promise<CategoryMappingEntry[]> Array of mapping entries
   */
  async getCategoryMappings(calendarId: string, actorId: string): Promise<CategoryMappingEntry[]> {
    const response = await axios.get(
      `/api/v1/calendars/${encodeURIComponent(calendarId)}/following/${encodeURIComponent(actorId)}/category-mappings`,
    );
    return response.data;
  }

  /**
   * Replace all category mappings for a followed calendar actor.
   * @param calendarId The local calendar ID
   * @param actorId The followed actor identifier
   * @param mappings The new full set of category mappings
   * @returns Promise<CategoryMappingEntry[]> Updated mapping entries
   */
  async setCategoryMappings(
    calendarId: string,
    actorId: string,
    mappings: CategoryMappingEntry[],
  ): Promise<CategoryMappingEntry[]> {
    const response = await axios.put(
      `/api/v1/calendars/${encodeURIComponent(calendarId)}/following/${encodeURIComponent(actorId)}/category-mappings`,
      { mappings },
    );
    return response.data;
  }
}
